import { useCallback, useEffect, useState } from "react";
import { CardImg, Button } from "reactstrap";
import { debounce } from "lodash";
import { TableViewDialog } from "../style";
import { getProducts, addProducts } from "../../product/service";
import { randomUUID } from "../../common/service";
import {
  Card,
  CardGroup,
  CardImgContainer,
  CardProductName,
  CardProductCode,
  SearchInput,
} from "../../product/style";

const AddDirectModuleDialog = ({
  setAddedProductLoading,
  refreshProjectSummary,
  currentRoom,
  projectId,
  setShowAddDirectModules,
  replaceModule,
}) => {
  const [searchText, setSearchText] = useState("");
  const [products, setProducts] = useState([]);
  const [isSearching, setIsSearching] = useState(true);
  const [selectedSubProducts, setSelectedSubProducts] = useState([]);

  const debounceSearch = useCallback(
    debounce((searchText) => {
      searchProducts(searchText);
    }, 500),
    []
  );

  const addDirectModules = (item) => {
    currentRoom.modules.push(item);
  };

  const handleAddItemToSection = (productsToAdd) => {
    if (!productsToAdd?.length) return;

    setShowAddDirectModules(false);

    const tempModules = productsToAdd.map((product) => {
      const tempId = randomUUID();
      product.temp_id = tempId;

      const tempItem = {
        id: tempId,
        temp_id: tempId,
        name: "Temporary Item",
        isTemporary: true,
        dimensions: {
          depth: 550,
          height: 1000,
          width: 833,
        },
        dimensions_overriden: {},
        position: { x: 0, y: 0 },
        propertyList: [],
      };

      addDirectModules(tempItem);
      return tempItem;
    });

    addProducts({
      productIds: productsToAdd.map((p) => p.id),
      tempIds: productsToAdd.map((p) => p.temp_id),
      projectId,
      roomId: currentRoom.id,
      position: { x: 0, y: 0 },
    })
      .then(({ order_items }) => {
        order_items.forEach((item) => replaceModule(item));
        refreshProjectSummary();
      })
      .finally(() => {
        setShowAddDirectModules(false);
      });
  };

  const handleProductClick = (product) => {
    if (product.sub_products?.length) {
      setSelectedSubProducts(product.sub_products);
    } else {
      handleAddItemToSection([product]);
    }
  };

  const handleSubProductClick = (subProduct) => {
    handleAddItemToSection([subProduct]);
  };

  const searchProducts = (searchText) => {
    setIsSearching(true);

    getProducts({
      searchTerm: searchText,
      defaultCatalogs: !searchText,
    })
      .then((products) => {
        const categoryKey = Object.values(products).flat();
        setProducts(categoryKey || []);
      })
      .catch(({ error }) => {
        console.error("Error fetching products:", error);
      })
      .finally(() => setIsSearching(false));
  };

  useEffect(() => {
    debounceSearch(searchText);
  }, [searchText]);

  useEffect(() => {
    searchProducts("");
  }, []);

  return (
    <TableViewDialog className="no-padding">
      <div className="p-2">
        <div className="p-1">
          <SearchInput
            autoFocus={true}
            className="unmask p-2"
            type="search"
            placeholder="Search Modules"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setSelectedSubProducts([]);
            }}
          />
        </div>

        {isSearching && !products?.length && (
          <div className="p-4 text-center mx-1 my-2 rounded-2 text-secondary fs-6">
            Searching...
          </div>
        )}

        <CardGroup
          className={`modules-listing row g-2 ${isSearching && "blurred"}`}
          tabIndex="-1"
          style={{
            width: "530px",
            overflowY: "auto",
            borderRadius: "0 0 5px 5px",
            maxHeight: "650px",
            margin: "0 -4px !important",
          }}
        >
          {products?.length > 0
            ? products.map((product, index) => (
                <div
                  className="col-3"
                  key={index}
                  style={{
                    position: "relative",
                  }}
                >
                  <Card
                    className={`module-card w-100 p-2 pointer`}
                    id={`disable-product-${index}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (product.can_add) {
                        handleProductClick(product);
                      }
                    }}
                  >
                    <CardImgContainer>
                      <CardImg
                        className="object-fit-contain"
                        src={product.images?.[0]}
                        alt={product.name}
                        style={{
                          opacity: product.can_add ? "1" : "0.3",
                        }}
                      />
                    </CardImgContainer>
                    <div className="pt-2 d-flex flex-column row-gap-1">
                      <CardProductCode>{product.sku}</CardProductCode>
                      <CardProductName>{product.name}</CardProductName>
                    </div>
                  </Card>
                </div>
              ))
            : !isSearching && (
                <div className="text-center my-5">
                  No modules found! Try a different keyword.
                </div>
              )}
        </CardGroup>

        {selectedSubProducts.length > 0 && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 bg-light-grey overflow-auto shadow-lg rounded"
            style={{ zIndex: 1000 }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3 p-3 border-bottom border-secondary-subtle">
              <h6 className="m-0 text-muted">Choose a Variant</h6>
              <Button
                size="sm"
                color="primary"
                onClick={() => setSelectedSubProducts([])}
              >
                Back
              </Button>
            </div>
            <div className="row g-2 mx-auto p-3" style={{ maxWidth: "530px" }}>
              {selectedSubProducts.map((subProduct, index) => (
                <div className="col-3" key={index}>
                  <Card
                    className="module-card w-100 p-2 pointer"
                    onClick={() => handleSubProductClick(subProduct)}
                  >
                    <CardImgContainer>
                      <CardImg
                        className="object-fit-contain"
                        src={subProduct.images?.[0]}
                        alt={subProduct.name}
                      />
                    </CardImgContainer>
                    <div className="pt-2 d-flex flex-column row-gap-1">
                      <CardProductCode>{subProduct.sku}</CardProductCode>
                      <CardProductName>{subProduct.name}</CardProductName>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </TableViewDialog>
  );
};

export default AddDirectModuleDialog;
